import classNames from 'classnames';
import { formatDate } from '../incomingInvoiceDateUtils';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  getOffice,
  getProject,
  RootReducerState,
} from '../../../../../../apps/main/rootReducer';
import Flex from '../../../../../../components/Flex';
import { Office } from '../../../../../../models/Office';
import { Project } from '../../../../../../models/Project';
import { makePrioStyles } from '../../../../../../theme/utils';
import { getCurrencySymbol } from '../../../../../../util/helpers';
import { useIncomingInvoiceDrawerContext } from '../incomingInvoiceComponents/IncomingInvoiceContextProvider';
import IncomingInvoiceDrawerSectionHeader from '../incomingInvoiceComponents/IncomingInvoiceDrawerSectionHeader';
import {
  FinancialDocumentAllocation,
  IncomingInvoiceDto,
} from '../../../../../../models/IncomingInvoices';
import { useProjectPhases } from '../hooks/useProjectPhase';
import { ProjectPhase } from '../../../../../../models/ProjectPhase';
import { Table } from 'antd';

const useStyles = makePrioStyles((theme) => ({
  root: {},
  overviewGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
    rowGap: theme.spacing.regular,
    columnGap: theme.spacing.small,
  },
  overviewGridHalf: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    rowGap: theme.spacing.regular,
    columnGap: theme.spacing.small,
  },
  overviewLabel: {
    fontSize: theme.font.fontSize.small,
    color: theme.colors.application.typography.muted,
  },
  overviewLabelBold: {
    fontSize: theme.font.fontSize.small,
    color: theme.colors.application.typography.default,
    fontWeight: theme.font.fontWeight.bold,
  },
  overviewValue: {
    fontSize: theme.font.fontSize.small,
    color: theme.colors.application.typography.default,
  },
  overviewMissingData: {
    fontSize: theme.font.fontSize.small,
    color: theme.colors.base.red[80],
  },
  colSpan2: { gridColumn: 'span 2' },
  colSpanFull: { gridColumn: '1 / -1' },
  allocationTable: {
    '& .ant-table': {
      backgroundColor: 'transparent',
    },
    '& .ant-table-thead': {
      display: 'none',
    },
    '& .ant-table-tbody > tr:hover': {
      backgroundColor: 'transparent !important',
    },
    '& .ant-table-tbody > tr:hover > td': {
      backgroundColor: 'transparent !important',
    },
    '& .ant-table-tbody .ant-table-cell': {
      paddingLeft: '0 !important',
      paddingRight: '0 !important',
      paddingTop: `${theme.spacing.small}px !important`,
      paddingBottom: `${theme.spacing.small}px !important`,
    },
    '& .ant-table-tbody tr.project-header .ant-table-cell': {
      paddingBottom: `0px !important`,
    },
    '& .ant-table-tbody tr.project-phase .ant-table-cell': {
      paddingTop: `0px !important`,
      paddingBottom: `0px !important`,
    },
    '& .ant-table-tbody > tr > td': {
      borderBottom: 'none !important',
      borderTop: 'none !important',
    },
    '& .ant-table-tbody > tr': {
      borderBottom: 'none !important',
    },
  },
  allocationProject: {
    fontSize: theme.font.fontSize.small,
    color: theme.colors.application.typography.default,
  },
  allocationPhase: {
    fontSize: theme.font.fontSize.small,
    color: theme.colors.application.typography.muted,
    fontStyle: 'italic',
  },
}));

const AddressBlock: React.FC<{
  office?: Office;
  missing: string;
  className?: string;
}> = ({ office, missing, className }) => {
  const classes = useStyles();
  if (office?.name) {
    const zipCity = [office.zip, office.city].filter(Boolean).join(' ');
    return (
      <Flex.Column className={className}>
        <span>{office.name}</span>
        {office.street && <span>{office.street}</span>}
        {zipCity && <span>{zipCity}</span>}
      </Flex.Column>
    );
  }
  return <span className={classes.overviewMissingData}>{missing}</span>;
};

const OverviewItem: React.FC<{
  label: string;
  value?: React.ReactNode;
  missing?: string;
  className?: string;
}> = ({ label, value, missing, className }) => {
  const classes = useStyles();
  return (
    <Flex.Column className={className}>
      <span className={classes.overviewLabel}>{label}</span>
      <span className={classes.overviewValue}>
        {value ?? (
          <span className={classes.overviewMissingData}>{missing}</span>
        )}
      </span>
    </Flex.Column>
  );
};

interface GroupedAllocation {
  projectId: string;
  project?: Project;
  phases: {
    projectPhase?: ProjectPhase;
    allocations: Partial<FinancialDocumentAllocation>[];
    totalAmount: number;
  }[];
  totalAmount: number;
}
interface IncomingInvoicesDrawerOverviewStepProps {
  className?: string;
  skipAllocationStep?: boolean;
}

export const IncomingInvoicesDrawerOverviewStep: React.FC<
  IncomingInvoicesDrawerOverviewStepProps
> = ({ className, skipAllocationStep = false }) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const { form, invoiceAllocations } = useIncomingInvoiceDrawerContext();

  const allValues = form.getFieldsValue(true) as Partial<IncomingInvoiceDto>;

  const {
    sharedWithProjectIds = [],
    fromOfficeId,
    fromOfficeName,
    toOfficeId,
    notes,
    totalExcludingVat,
    totalIncludingVat,
    amountToTransfer,
    currency,
    cashDiscountPercent,
    cashDiscountDays,
    periodOfPerformanceFrom,
    periodOfPerformanceTo,
    date,
    dueDate,
  } = allValues;
  //#endregion

  //#region ------------------------------ Methods / Handlers

  const projects = useSelector<RootReducerState, Project[]>((state) =>
    sharedWithProjectIds
      .map((id: string) => getProject(state, id))
      .filter((p): p is Project => !!p)
  );

  const projectPhaseQueries = useMemo(() => {
    const queries: { projectId: string; projectPhaseId: string }[] = [];

    invoiceAllocations?.forEach((allocation) => {
      if (allocation.projectId && allocation.projectPhaseId) {
        const exists = queries.some(
          (q) =>
            q.projectId === allocation.projectId &&
            q.projectPhaseId === allocation.projectPhaseId
        );

        if (!exists) {
          queries.push({
            projectId: allocation.projectId,
            projectPhaseId: allocation.projectPhaseId,
          });
        }
      }
    });

    return queries;
  }, [invoiceAllocations]);

  const { data: projectPhases } = useProjectPhases(projectPhaseQueries);

  const groupedAllocations = useMemo(() => {
    const groups: Record<string, GroupedAllocation> = {};

    invoiceAllocations?.forEach((allocation) => {
      const projectId = allocation.projectId;
      if (!projectId) return;

      if (!groups[projectId]) {
        groups[projectId] = {
          projectId,
          project: projects.find((p) => p.projectId === projectId),
          phases: [],
          totalAmount: 0,
        };
      }

      if (allocation.projectPhaseId) {
        const projectPhaseId = allocation.projectPhaseId;
        let phase = groups[projectId].phases.find(
          (p) => p.projectPhase?.projectPhaseId === projectPhaseId
        );

        if (!phase) {
          const projectPhase = projectPhases.find(
            (pp) =>
              pp.projectPhaseId === allocation.projectPhaseId &&
              pp.projectId === allocation.projectId
          );

          if (projectPhase) {
            phase = {
              projectPhase,
              allocations: [],
              totalAmount: 0,
            };
            groups[projectId].phases.push(phase);
          }
        }

        if (phase) {
          phase.allocations.push(allocation);
          phase.totalAmount += allocation.amount || 0;
        }
      }

      groups[projectId].totalAmount += allocation.amount || 0;
    });

    Object.values(groups).forEach((group) => {
      if (group.phases.length > 0) {
        const phasesTotal = group.phases.reduce(
          (sum, phase) => sum + phase.totalAmount,
          0
        );
        const withoutPhaseAmount = group.totalAmount - phasesTotal;

        if (withoutPhaseAmount > 0) {
          group.phases.push({
            projectPhase: undefined,
            allocations: [],
            totalAmount: withoutPhaseAmount,
          });
        }
      }
    });

    const sortedGroups = Object.values(groups).sort((a, b) => {
      const projectA = a.project?.number || '';
      const projectB = b.project?.number || '';
      return projectA.localeCompare(projectB);
    });

    sortedGroups.forEach((group) => {
      group.phases.sort((a, b) => {
        if (!a.projectPhase) return 1;
        if (!b.projectPhase) return -1;

        const phaseA = a.projectPhase?.name || '';
        const phaseB = b.projectPhase?.name || '';
        return phaseA.localeCompare(phaseB);
      });
    });

    return sortedGroups;
  }, [invoiceAllocations, projects, projectPhases]);

  const allocationTableData = useMemo(() => {
    const rows: Array<{
      key: string;
      project: string;
      projectPhase: string;
      amount: number;
      formattedAmount: string;
      isProjectHeader?: boolean;
      projectTotal?: number;
    }> = [];

    groupedAllocations.forEach((group) => {
      const projectName = group.project
        ? `${group.project.number} ${group.project.name}`
        : `Projekt ${group.projectId}`;

      if (group.phases.length > 0) {
        rows.push({
          key: `${group.projectId}-header`,
          project: projectName,
          projectPhase: '',
          amount: group.totalAmount,
          formattedAmount: `${group.totalAmount.toFixed(2)} ${getCurrencySymbol(currency, i18n.language)}`,
          isProjectHeader: true,
          projectTotal: group.totalAmount,
        });

        group.phases.forEach((phase, phaseIndex) => {
          rows.push({
            key: `${group.projectId}-${phaseIndex}`,
            project: `└─ ${
              phase.projectPhase?.name ||
              t('accounting:incomingInvoices.fields.withoutProjectPhase.label')
            }`,
            projectPhase: '',
            amount: phase.totalAmount,
            formattedAmount: `${phase.totalAmount.toFixed(2)} ${getCurrencySymbol(currency, i18n.language)}`,
          });
        });
      } else {
        rows.push({
          key: group.projectId,
          project: projectName,
          projectPhase: '-',
          amount: group.totalAmount,
          formattedAmount: `${group.totalAmount.toFixed(2)} ${getCurrencySymbol(currency, i18n.language)}`,
          isProjectHeader: true,
          projectTotal: group.totalAmount,
        });
      }
    });

    return rows;
  }, [groupedAllocations, currency, i18n.language, t]);

  const allocationColumns = [
    {
      id: 'project',
      accessor: 'project',
      width: 70,
      render: (text: string, record: any) => (
        <span
          className={classNames(
            record.isProjectHeader
              ? classes.allocationProject
              : classes.allocationPhase
          )}
        >
          {record.project}
        </span>
      ),
    },
    {
      id: 'amount',
      accessor: 'amount',
      width: 30,
      align: 'right' as const,
      render: (text: string, record: any) => (
        <span
          className={classNames(
            record.isProjectHeader
              ? classes.allocationProject
              : classes.allocationPhase
          )}
        >
          {record.formattedAmount}
        </span>
      ),
    },
  ];

  const billingOffice = useSelector<RootReducerState, Office | undefined>(
    (state) => getOffice(state, fromOfficeId)
  );
  const recipientOffice = useSelector<RootReducerState, Office | undefined>(
    (state) => getOffice(state, toOfficeId)
  );

  const currencySymbol = useMemo(
    () => getCurrencySymbol(currency, i18n.language),
    [currency, i18n.language]
  );
  //#endregion

  return (
    <Flex.Column className={className}>
      {/* --- Internal Data --- */}
      {/* Only show Internal Data section if there are allocations or notes */}
      {(!skipAllocationStep || notes) && (
        <div>
          <IncomingInvoiceDrawerSectionHeader
            title={t(
              'accounting:incomingInvoices.drawerSteps.overview.sectionTitles.internalData'
            )}
            iconProp={['fal', 'circle-dashed']}
            disableEdit
          />
          <div className={classes.overviewGrid}>
            {!skipAllocationStep && (
              <OverviewItem
                label={t(
                  'accounting:incomingInvoices.fields.costAllocations.label'
                )}
                value={
                  groupedAllocations.length > 0 ? (
                    <div>
                      <Table
                        className={classes.allocationTable}
                        dataSource={allocationTableData}
                        columns={allocationColumns}
                        pagination={false}
                        size="small"
                        rowClassName={(record) =>
                          record.isProjectHeader
                            ? 'project-header'
                            : 'project-phase'
                        }
                      />
                    </div>
                  ) : undefined
                }
                missing={t('accounting:incomingInvoices.notAssigned')}
                className={classNames(classes.colSpanFull)}
              />
            )}
            {notes && (
              <OverviewItem
                label={t('accounting:incomingInvoices.fields.remarks.label')}
                value={notes}
                className={classes.colSpanFull}
              />
            )}
          </div>
        </div>
      )}

      {/* --- Invoice Overview --- */}
      <div>
        <IncomingInvoiceDrawerSectionHeader
          title={t(
            'accounting:incomingInvoices.drawerSteps.overview.sectionTitles.invoiceOverview'
          )}
          iconProp={['fal', 'file-invoice']}
          disableEdit
        />
        <div className={classes.overviewGrid}>
          <div
            className={classNames(classes.colSpanFull, classes.overviewGridHalf)}
          >
            <OverviewItem
              label={t('accounting:incomingInvoices.fields.fromOfficeId.label')}
              value={
                billingOffice ? (
                  <AddressBlock
                    office={billingOffice}
                    missing={t('accounting:incomingInvoices.noData')}
                  />
                ) : (
                  fromOfficeName
                )
              }
            />
            <OverviewItem
              label={t('accounting:incomingInvoices.fields.toOfficeId.label')}
              value={
                <AddressBlock
                  office={recipientOffice}
                  missing={t('accounting:incomingInvoices.noData')}
                />
              }
            />
          </div>

          <div
            className={classNames(classes.colSpanFull, classes.overviewGridHalf)}
          >
            <OverviewItem
              label={t('accounting:incomingInvoices.fields.date.label')}
              value={formatDate(date)}
              missing={t('accounting:incomingInvoices.noData')}
            />
            <OverviewItem
              label={t('accounting:incomingInvoices.fields.dueDate.label')}
              value={formatDate(dueDate)}
              missing={t('accounting:incomingInvoices.noData')}
            />
          </div>

          <OverviewItem
            label={t(
              'accounting:incomingInvoices.fields.totalExcludingVat.label'
            )}
            value={
              totalExcludingVat
                ? totalExcludingVat?.toFixed(2) + ' ' + currencySymbol
                : undefined
            }
            missing={t('accounting:incomingInvoices.noData')}
          />
          <OverviewItem
            label={t(
              'accounting:incomingInvoices.fields.totalIncludingVat.label'
            )}
            value={
              totalIncludingVat
                ? totalIncludingVat?.toFixed(2) + ' ' + currencySymbol
                : undefined
            }
            missing={t('accounting:incomingInvoices.noData')}
          />
          <OverviewItem
            label={t(
              'accounting:incomingInvoices.fields.amountToTransfer.label'
            )}
            value={
              amountToTransfer
                ? amountToTransfer?.toFixed(2) + ' ' + currencySymbol
                : undefined
            }
            missing={t('accounting:incomingInvoices.noData')}
          />

          {cashDiscountPercent > 0 && (
            <OverviewItem
              label={t(
                'accounting:incomingInvoices.drawerSteps.overview.fields.discount'
              )}
              value={
                cashDiscountPercent != null && cashDiscountDays != null
                  ? `${cashDiscountPercent}% - ${cashDiscountDays} ${t('accounting:incomingInvoices.drawerSteps.overview.fields.days')}`
                  : undefined
              }
              missing={t('accounting:incomingInvoices.noData')}
            />
          )}

          <OverviewItem
            label={t(
              'accounting:incomingInvoices.fields.performancePeriod.label'
            )}
            value={
              formatDate(periodOfPerformanceFrom) &&
              formatDate(periodOfPerformanceTo)
                ? `${formatDate(periodOfPerformanceFrom)} - ${formatDate(periodOfPerformanceTo)}`
                : undefined
            }
            missing={t('accounting:incomingInvoices.noData')}
            className={classes.colSpan2}
          />
        </div>
      </div>
    </Flex.Column>
  );
};

export default IncomingInvoicesDrawerOverviewStep;
